import { useQuery } from '@tanstack/react-query'

import Api from 'services/Api'
import { Break } from 'Api/types'
import { hashTable } from 'services/Utilities'

import { CACHE_TIME, RESSOURCES_ENDPOINTS, STALE_TIME } from 'Api/constants'
import { queryClient } from 'services/QueryClient'

const breaksQuery = async () => {
  const breaks: { result: Break[]; count: number } = await Api({ url: RESSOURCES_ENDPOINTS.breaks, method: 'GET', params: { useNewReturn: true } })
  return breaksTransformer(breaks.result, breaks.count)
}

const breaksTransformer = (breaks: Break[], count: number) =>
  ({
    breaks,
    breaksIds: breaks?.map(({ _id }) => _id),
    breaksById: breaks?.length ? hashTable(breaks) : undefined,
    count: count ?? 0
  } as const)

const getQueryConfig = () =>
  ({
    queryKey: ['breaks'],
    queryFn: breaksQuery,
    cacheTime: CACHE_TIME,
    staleTime: STALE_TIME
  } as const)

type Select<T> = (data: BreaksData) => T

const useBreaks = <T = BreaksData>(select?: Select<T>) => {
  const config = getQueryConfig()
  return useQuery({ ...config, select })
}

const useBreakById = (id: string) => {
  return useBreaks(data => data.breaksById?.[id])
}

const prefetchBreaks = async () => queryClient.prefetchQuery({ ...getQueryConfig() })
const invalidateBreaks = async () => queryClient.invalidateQueries(getQueryConfig().queryKey)
const getBreaksQueryCache = () => queryClient.getQueryData<BreaksData>(getQueryConfig().queryKey)

type BreaksData = ReturnType<typeof breaksTransformer>

export type { BreaksData }
export { useBreaks, prefetchBreaks, invalidateBreaks, getBreaksQueryCache, useBreakById }
